//----------------------------
// create changed item entry
//----------------------------
export default function createChangedItemEntry({
  name = '', // item name
  type = '', // item type (used primarily to indicate artefacts for static item creation)
  display_name = '', // optional display name for item (if different from static item name)
  action = '', // 'ADD', 'UPDATE', 'DELETE'
  id = null, // for character-item updates/deletes (character-item id) / will be temp-id for new item (tracking only)
  payload = {},
} = {}) {
  //--------------------------------------------------------------------------
  // basic validation - name and action are required for a valid change entry
  //--------------------------------------------------------------------------
  if (!name || !action) {
    throw new Error('Invalid input: name and action are required')
  }
  //--------------------------------------
  // extract payload fields with defaults
  //--------------------------------------
  const {
    character = null, // for new character_item (character id)
    item = null, // for new character_item (static item id)
    tally_qty = 0,
    player_notes = payload?.notes ?? '',
    short_description = '',
    enc = 0,
    worn_location = null,
    attach_location = null,
    armour_location = null,
    container = null,
    unit_name = null,
    runes = [],
    non_rune_spells = [],
    ap = 0,
    hp = 0,
    pwr_storage = 0,
    held = false,
    equipped = false,
    max_unit_count = null,
    cur_unit_count = null,
    healing_type = null,
    recovers = null,
    poison_type = null,
    pot = null,
    effect = null,
    delay = null,
    damage_dice = null,
    duration = null,
    resist = null,
  } = payload || {}
  //---------------------------------------------
  // resolve ids (explicit null if not provided)
  //---------------------------------------------
  const resolvedItemId = item ?? null
  const resolvedCharacterId = character ?? null
  //--------------------------------------------------------------------------
  // build output object progressively with clear intent for each action type
  //--------------------------------------------------------------------------
  const out = {
    name,
    type,
    action,
  }
  //---------------------------------------------------------------------
  // for ADD & UPDATE: include payload object describing editable fields
  //---------------------------------------------------------------------
  if (action === 'ADD' || action === 'UPDATE') {
    const outPayload = {
      tally_qty,
      player_notes,
      short_description,
      enc,
      worn_location,
      attach_location,
      armour_location,
      container,
      unit_name,
      runes,
      non_rune_spells,
      ap,
      hp,
      pwr_storage,
      held,
      equipped,
      max_unit_count,
      cur_unit_count,
      healing_type,
      recovers,
      poison_type,
      pot,
      effect,
      delay,
      damage_dice,
      duration,
      resist,
    }
    //-------------------------------------------------------------------------------------------------
    // add display name to payload if provided (e.g. 'Pouch 2') - used for id matching during db saves
    //-------------------------------------------------------------------------------------------------
    if (display_name !== '') {
      outPayload.display_name = display_name
    }
    //--------------------------------------------------------------------
    // for ADD: also include static item id & character id inside payload
    //--------------------------------------------------------------------
    if (action === 'ADD') {
      outPayload.item = resolvedItemId
      outPayload.character = resolvedCharacterId
    }
    //-------------------------------------
    // assign constructed payload to output
    //--------------------------------------
    out.payload = outPayload
  } else if (
    action === 'DELETE' &&
    payload &&
    typeof payload === 'object' &&
    Object.keys(payload).length > 0
  ) {
    out.payload = { ...payload }
  }
  //-----------------------------------------------------------------------------------
  // UPDATE & DELETE: id = character-item id
  // ADD: id = temp id for tracking (actual character-id + static item-id provided inside payload for server-side creation)
  //-----------------------------------------------------------------------------------
  if (id != null) {
    out.id = id
  }
  //----------------
  // return results
  //----------------
  return out
}
